import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CenteredSpinner } from '@l2u/ui/components/Spinner.tsx';
import { AuthNotice } from '@l2u/ui/components/AuthSplitLayout.tsx';
import { getSupabase } from '@l2u/ui/lib/supabase.ts';
import { setCompanyAccessToken } from '@l2u/ui/lib/auth/companyToken.ts';
import { parseAuthErrorFromUrl } from '@l2u/ui/lib/authErrorFromUrl.ts';

// Lands here after the Supabase magic link verifies. Picks up the session
// (the company client has detectSessionInUrl: true, with a hash fallback) and
// forwards into the portal.
export function CompanyCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<unknown>(null);
  const [linkMessage, setLinkMessage] = useState<string | null>(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    // An expired/used link carries no token — Supabase appends an error
    // instead (e.g. #error=access_denied&error_code=otp_expired). Surface a
    // friendly message rather than falling through to the technical
    // "No session in callback URL" below.
    const authError = parseAuthErrorFromUrl(window.location.search, window.location.hash);
    if (authError) {
      setLinkMessage(
        authError.code === 'otp_expired'
          ? 'Your sign-in link expired or was already used. Request a new one from the sign-in page.'
          : (authError.description ??
              'Your sign-in link could not be used. Request a new one from the sign-in page.'),
      );
      return;
    }

    let supabase;
    try {
      supabase = getSupabase();
    } catch (err) {
      setError(err);
      return;
    }

    void supabase.auth
      .getSession()
      .then(({ data }) => {
        if (data.session) {
          setCompanyAccessToken(data.session.access_token);
          window.history.replaceState({}, '', '/');
          navigate('/', { replace: true });
          return;
        }

        const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        const access = fragment.get('access_token');
        const refresh = fragment.get('refresh_token');
        if (!access || !refresh) {
          throw new Error('No session in callback URL');
        }
        return supabase.auth
          .setSession({ access_token: access, refresh_token: refresh })
          .then(({ error: setErr }) => {
            if (setErr) throw setErr;
            setCompanyAccessToken(access);
            window.history.replaceState({}, '', '/');
            navigate('/', { replace: true });
          });
      })
      .catch((err) => setError(err));
  }, [navigate]);

  if (linkMessage) {
    return (
      <AuthNotice variant="vendor" title="We couldn’t sign you in">
        {linkMessage}
      </AuthNotice>
    );
  }
  if (error) {
    return (
      <AuthNotice variant="vendor" title="We couldn’t sign you in" error={error}>
        Something went wrong finishing your sign-in. Try your link again, or request a new one from
        the sign-in page.
      </AuthNotice>
    );
  }
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50">
      <CenteredSpinner />
      <p className="text-sm text-slate-500">Signing you in…</p>
    </div>
  );
}
